import { authFetch } from "./api"
import type { Company } from "./company"

export type Task = {
  id: string,
  scope: string
  role: string
  companyId: string
  companySapCode: string
  companyName: string
  description: string
  currentStatus: EnumStatus
  deadline: string
  completionTime: string | null
  issue: Issue | null
  status: EnumStatus,
  assignees?: string[]
  reminder1: string
  reminder2: string
}


export enum EnumStatus {
  Completed = "Completed",
  WorkInProgress = "WorkInProgress",
  Reopen = "Reopen",
  NotStarted = "NotStarted",
  Critical = "Critical",
  NotApplicable = "NotApplicable",
}

export enum Issue {
  ClientDependency = "Client dependency",
  InfoNotAvailable = "Info not Available",
  PreviousDependencyInClosingProcess = "Previous dependency in closing process",
  InternalIssue = "Internal issue",
}


export type CreateTaskDTO = {
  scope: string
  description: string
  role: string
  companyId: string | null
  deadline: string
  reminder1?: string | null
  reminder2?: string | null
  keyDeadline?: boolean
}

export type UpdateTaskDTO = {
  scope: string
  description: string
  role: string
  companyId: string
  deadline: string
  reminder1?: string | null
  reminder2?: string | null
  keyDeadline?: boolean
}

export type Assignee = {
  name: string
  email: string
}; 

export type TaskProps = {
  roles: string[]
  scopes: string[]
  descriptions: string[],
  companies: Company[]
  assignees: Assignee[]
}

export type PageResult<T> = {
  items: T[],
  totalCount: number
  page: number,
  pageSize: number
}

export type PageRequest = {
  page: string
  pageSize: string
}

export type ClosingPeriod = {
  month: string
  year: string
}

export type TaskListParams = {
  page?: PageRequest
  sort?: string
  order?: "asc" | "desc"
  closingPeriod?: ClosingPeriod
  overdue?: boolean
  source: string,
  includeAssignee?: boolean,
  excludeNotApplicable?: boolean
  filters: Record<string, any>
}

export const getTasks = async ({month, year, day}: { month?: number, year?: number, day?: number}) => {
  try {
    let url = `${import.meta.env.VITE_API_URL}/activities`
    const queryParams = new URLSearchParams()

    if (month) queryParams.append("month", String(month)) // I mesi sono indicizzati da 0
    if (year) queryParams.append("year", String(year))
    if (day) queryParams.append("day", String(day)) // Usa il giorno nella query

    if (queryParams.toString()) {
      url += `?${queryParams.toString()}`
    }
    
    const response = await authFetch(url)

    const json = await response.json()
    if (!response.ok) {
      console.error("Errore durante il recupero delle attività:", json)
      return { data: null, error: json.message || "Errore sconosciuto" }
    }
    return  { data: json, error: null}

  } catch (error) {
    console.error("Errore durante il recupero delle attività:", error)
    return { data: null, error: error instanceof Error ? error.message : "Errore sconosciuto" }
  }
}

// export const adminGetTasks = async (data: any) => {
//   try {
//     let url = `${import.meta.env.VITE_API_URL}/admin/activities/list`
   
//     const response = await authFetch(url, {
//       method: "POST",
//       body: JSON.stringify(data),
//       headers: {
//         'Content-Type': 'application/json'
//       }
//     });

//     const json = await response.json() as AdminTasksResponse
//     if (!response.ok) {
//       console.error("Errore durante il recupero delle attività:", json)
//       return { data: null, error: "Errore sconosciuto" }
//     }
//     return  { data: json, error: null}

//   } catch (error) {
//     console.error("Errore durante il recupero delle attività:", error)
//     return { data: null, error: error instanceof Error ? error.message : "Errore sconosciuto" }
//   }
// }

export const getTasksProps = async () => {
  try {
    const response = await authFetch(`${import.meta.env.VITE_API_URL}/admin/activities/props`)
    const json = await response.json()
    if (!response.ok) {
      console.error("Errore durante il recupero delle proprietà delle attività:", json)
      return { data: null, error: json.message || "Errore sconosciuto" }
    }
    return { data: json as TaskProps, error: null }
  } catch (error) {
    console.error("Errore durante il recupero delle proprietà delle attività:", error)
    return { data: null, error: error instanceof Error ? error.message : "Errore sconosciuto" }
  }
}

export const getTaskDetails = async (id: string) => {
  try {
    const response = await authFetch(`${import.meta.env.VITE_API_URL}/admin/activities/${id}`)
    const json = await response.json()
    if (!response.ok) {
      console.error("Errore durante il recupero dei dettagli dell'attività:", json)
      return { data: null, error: json.message || "Errore sconosciuto" }
    }
    return { data: json as Task, error: null }
  } catch (error) {
    console.error("Errore durante il recupero dei dettagli dell'attività:", error)
    return { data: null, error: error instanceof Error ? error.message : "Errore sconosciuto" }
  }
}

export const postCreateActivity = async (data: CreateTaskDTO) => {
  try {
    const response = await authFetch(`${import.meta.env.VITE_API_URL}/admin/activities`, {
      method: "POST",
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'application/json'
      }
    });
    const json = await response.json();
    if (!response.ok) {
      return { data: null, error: response.statusText || "Errore sconosciuto" };
    }
    return { data: json, error: null };
  } catch (error) {
    console.error("Errore durante la creazione dell'attività:", error);
    return { data: null, error: error instanceof Error ? error.message : "Errore sconosciuto" };
  }
}

export const putUpdateActivity = async (id: string, data: UpdateTaskDTO) => {
  try {
    const response = await authFetch(`${import.meta.env.VITE_API_URL}/admin/activities/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'application/json'
      }
    });
    if (!response.ok) {
      return {  error: response.statusText || "Errore sconosciuto" };
    }
    return { error: null };
  } catch (error) {
    console.error("Errore durante la creazione dell'attività:", error);
    return { error: error instanceof Error ? error.message : "Errore sconosciuto" };
  }
}

// services/api.ts
export const putStatusUpdate = async (taskId: string, newStatus: string, issue?: string): Promise<{ error: string | null}> => {
  try {

    const response = await authFetch(`${import.meta.env.VITE_API_URL}/activities`, {
      method: "PUT",
      body: JSON.stringify({
        status: newStatus,
        issue: issue,
        taskId: taskId
      })
    })

    const json = await response.json()
    
    if (!response.ok) {
      
      return {error: json.message}
    }
    return {error: null}
  } catch (error) {
    if (error instanceof Error) {
      return { error: error.message }
    }
    return { error: "Errore sconosciuto" }
  }
}

export const getTasksList = async (data: TaskListParams) => {
  try {
    const url = `${import.meta.env.VITE_API_URL}/activities`

    const response = await authFetch(url, {
      method: "POST",
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'application/json'
      }
    })

    const json = await response.json() as PageResult<Task>
    if (!response.ok) {
      console.error("Errore durante il recupero delle attività:", json)
      return { data: null, error: "Errore sconosciuto" }
    }

    return { data: json, error: null }
  } catch (error) {
    console.error("Errore durante il recupero delle attività:", error)
    return { data: null, error: error instanceof Error ? error.message : "Errore sconosciuto" }
  }
}